import { Button } from '@heidi/design-system/button';
import { TierProFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { X } from 'lucide-react';
import { type ReactNode, type Ref } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS } from '../clinician-max-upgrade-theme';
import { formatComplimentaryTrialEndDate } from '../complimentary-trial-end/format-complimentary-trial-end-date';
import type { ClinicianTrialEndPhase } from './clinician-trial-end-state';
import { clinicianTrialNavCardMessages as messages } from './clinician-trial-nav-card.messages';

export type ClinicianTrialNavCardProps = {
  phase: Exclude<ClinicianTrialEndPhase, 'ended'>;
  daysLeft: number;
  trialEnd: string;
  learnMoreHref: string;
  onUpgrade: () => void;
  /** Present until the member has completed the trial extension checklist. */
  onExtendTrial?: () => void;
  onDismiss: () => void;
  upgradeButtonRef?: Ref<HTMLButtonElement>;
};

/** Sidebar card for a new user's Clinician trial, opened from the trial tile. */
export function ClinicianTrialNavCard({
  phase,
  daysLeft,
  trialEnd,
  learnMoreHref,
  onUpgrade,
  onExtendTrial,
  onDismiss,
  upgradeButtonRef,
}: ClinicianTrialNavCardProps) {
  const intl = useIntl();
  const learnMore = (chunks: ReactNode) => (
    <Link
      variant="inline"
      href={learnMoreHref}
      target="_blank"
      rel="noopener noreferrer"
      className="text-text-primary decoration-text-primary/50 hover:text-text-primary hover:decoration-text-primary"
    >
      {chunks}
    </Link>
  );

  return (
    <div
      role="region"
      aria-label={intl.formatMessage(messages.region)}
      className="relative flex w-60 max-w-[calc(100vw-2rem)] flex-col gap-4 p-4"
      data-testid="clinician-trial-nav-card"
    >
      <div className="flex flex-col gap-1 pe-5">
        <TypographyP2SemiBold className="text-text-primary">
          <FormattedMessage {...messages.title} values={{ count: daysLeft }} />
        </TypographyP2SemiBold>
        <TypographyCaption className="text-text-primary">
          <FormattedMessage
            {...(phase === 'last_7' ? messages.bodyLastWeek : messages.body)}
            values={{ date: formatComplimentaryTrialEndDate(intl, trialEnd), learnMore }}
          />
        </TypographyCaption>
      </div>
      <div className="flex flex-col gap-2">
        <Button
          ref={upgradeButtonRef}
          type="button"
          variant="secondary"
          size="md"
          className={`w-full gap-x-1.5 ${CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS}`}
          onClick={onUpgrade}
          data-testid="clinician-trial-nav-card-upgrade"
        >
          <TierProFilled size={14} className="shrink-0" />
          <FormattedMessage {...messages.upgrade} />
        </Button>
        {onExtendTrial ? (
          <Button
            type="button"
            variant="outline"
            size="md"
            className="w-full"
            onClick={onExtendTrial}
            data-testid="clinician-trial-nav-card-extend-trial"
          >
            <FormattedMessage {...messages.extendTrial} />
          </Button>
        ) : null}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="xxs"
        iconOnly
        aria-label={intl.formatMessage(messages.close)}
        className="absolute end-3 top-3 text-text-secondary"
        onClick={onDismiss}
        data-testid="clinician-trial-nav-card-close"
      >
        <X />
      </Button>
    </div>
  );
}
